/**
 * One ranked row. Structural rather than a named wire type, so both
 * `EventBreakdown` and `SignupSourceBreakdown` satisfy it as they are — which
 * is why they both spell the identifier `key`. A row shape of its own would
 * mean a mapping step at each call site whose only job is renaming a field.
 */
interface LedgerRow {
  key: string;
  label: string;
  count: number;
  /** 0-100. Announced to a screen reader; the bar is scaled to the leader. */
  percentage: number;
}

/**
 * What competes for one denominator, ranked.
 *
 * Built as a cue sheet rather than another `AdoptionBars`: that component is a
 * set of independent meters, each with its own denominator, so a percentage is
 * the whole point there. These rows share one denominator and compete for it,
 * so the question is order — which entry is first, and how far ahead of the
 * next one it is.
 *
 * The rank numbers are the reason the numbering is here at all: order carries
 * information in this list, unlike the numbered markers that decorate a set of
 * unordered features. Read down the column and you are reading the answer.
 *
 * Levels are scaled to the leader, not to the total. Scaled to the total, a
 * top entry holding a third of everything would draw a bar a third full and
 * everything below it would collapse into indistinguishable slivers — the
 * comparison the list exists to make would be the one it renders worst.
 *
 * `unit` carries both forms rather than a plural pluralised by rule: the count
 * announced to a screen reader is often 1, and "1 signups" is the kind of
 * wrong that only ever reaches the people who cannot see the number beside it.
 */
export function Ledger({
  rows,
  unit,
}: {
  rows: readonly LedgerRow[];
  unit: { one: string; many: string };
}) {
  if (rows.length === 0) {
    return <p className="muted">No {unit.many} counted in this window.</p>;
  }

  const leader = Math.max(1, ...rows.map((entry) => entry.count));

  return (
    <ol className="ledger">
      {rows.map((entry, index) => (
        <li className="ledger-row" key={entry.key}>
          <span className="ledger-rank" aria-hidden="true">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="ledger-label">{entry.label}</span>
          {/* The leader dots are the rule that ties a label to its number
              across a wide row — a cue sheet's own device, and the reason the
              count can sit hard right where it is easy to compare down the
              column. */}
          <span className="ledger-leader" aria-hidden="true" />
          <span className="ledger-count">{entry.count.toLocaleString()}</span>
          <span
            className="ledger-level"
            role="meter"
            aria-valuenow={entry.percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${entry.label}: ${entry.count.toLocaleString()} ${
              entry.count === 1 ? unit.one : unit.many
            }, ${entry.percentage}% of all counted`}
          >
            <span className="ledger-fill" style={{ width: `${(entry.count / leader) * 100}%` }} />
          </span>
        </li>
      ))}
    </ol>
  );
}
